import { Badge, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { DataTable, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Button } from '@vnetwork/ui/primitives';
import type { OpsServiceDetail, OpsServiceZoneRow, OpsCategory } from '@/api/operations';
import { formatDateTime } from '@/lib/format';
import {
  InfoGrid,
  SectionLabel,
  STATUS_TONE,
  STATUS_TOOLTIP,
  CATEGORY_TONE,
  CATEGORY_LABEL,
  type InfoRow,
} from './ops-detail-shared';
import { EntityLink } from '@/components/entity-link';

/* ── Zone table columns ──────────────────────────────────────────────────── */

const zoneColumns: ColumnDef<OpsServiceZoneRow, unknown>[] = [
  {
    id: 'uid',
    accessorKey: 'uid',
    header: 'ID',
    cell: ({ row }) => (
      <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)' }}>
        {row.original.uid || '—'}
      </span>
    ),
  },
  {
    id: 'zone_name',
    header: 'Zone',
    cell: ({ row }) => row.original.zone?.zone_name ?? '—',
  },
  {
    id: 'api_endpoint',
    header: 'API Endpoint',
    cell: ({ row }) => (
      <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
        {row.original.zone?.api_endpoint ?? '—'}
      </span>
    ),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const tone = STATUS_TONE[row.original.status] ?? 'neutral';
      const tip = STATUS_TOOLTIP[row.original.status] ?? row.original.status;
      return <StatusPill tone={tone} tooltip={tip}>{row.original.status}</StatusPill>;
    },
  },
];

/* ── Props ────────────────────────────────────────────────────────────────── */

interface OverviewTabProps {
  data: OpsServiceDetail | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/* ── Component ───────────────────────────────────────────────────────────── */

export function OverviewTab({ data, isLoading, isError, onRetry }: OverviewTabProps) {
  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load service detail"
        actions={
          <Button size="sm" variant="ghost" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    );
  }

  if (isLoading || !data) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card><CardBody><Skeleton style={{ height: 120 }} /></CardBody></Card>
        <Card><CardBody><Skeleton style={{ height: 80 }} /></CardBody></Card>
      </div>
    );
  }

  const statusTone = STATUS_TONE[data.status] ?? 'neutral';
  const statusTip = STATUS_TOOLTIP[data.status] ?? data.status;
  const catTone = data.provider
    ? (CATEGORY_TONE[data.provider.category as OpsCategory] ?? 'neutral')
    : 'neutral';
  const catLabel = data.provider
    ? (CATEGORY_LABEL[data.provider.category as OpsCategory] ?? data.provider.category)
    : '—';

  const serviceRows: InfoRow[] = [
    { label: 'Service name', value: <strong>{data.service_name}</strong> },
    {
      label: 'Service ID',
      value: <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)' }}>{data.id}</span>,
    },
    {
      label: 'Status',
      value: <StatusPill tone={statusTone} tooltip={statusTip}>{data.status}</StatusPill>,
    },
    { label: 'Created', value: formatDateTime(data.created_at) },
    { label: 'Updated', value: formatDateTime(data.updated_at) },
    { label: 'Domains', value: String(data.domain_count) },
  ];

  const orgRows: InfoRow[] = [
    {
      label: 'Organization',
      value: (
        <EntityLink
          to={`/operations/orgs/${data.organization_id}`}
          id={data.organization_id}
          name={data.customer?.customer_company_name || data.customer?.customer_name}
        />
      ),
    },
    ...(data.customer
      ? [
          {
            label: 'Customer',
            value: (
              <EntityLink
                to={`/customers/${data.customer.customer_id}`}
                id={data.customer.customer_id}
                name={data.customer.customer_company_name || data.customer.customer_name}
              />
            ),
          },
        ]
      : [{ label: 'Customer', value: <span style={{ color: 'var(--vn-fg-subtle)' }}>Not found in DataCentral</span> }]),
  ];

  const providerRows: InfoRow[] = data.provider
    ? [
        {
          label: 'Provider',
          value: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <span>{data.provider.name}</span>
              <Badge tone={catTone}>{catLabel}</Badge>
            </div>
          ),
        },
        { label: 'Display name', value: data.provider.display_name || '—' },
        {
          label: 'Scope',
          value: (
            <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)' }}>
              {data.provider.legacy_scope}
            </span>
          ),
        },
      ]
    : [{ label: 'Provider', value: <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span> }];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Service info card — 3 sections side by side */}
      <Card>
        <CardBody>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 'var(--vn-space-6)',
            }}
          >
            <div><SectionLabel>Service</SectionLabel><InfoGrid rows={serviceRows} /></div>
            <div><SectionLabel>Organization &amp; Customer</SectionLabel><InfoGrid rows={orgRows} /></div>
            <div><SectionLabel>Provider</SectionLabel><InfoGrid rows={providerRows} /></div>
          </div>
        </CardBody>
      </Card>

      {/* Service zones table — no credential fields */}
      <Card>
        <CardBody>
          <SectionLabel>Service Zones ({data.service_zones.length})</SectionLabel>
          <DataTable
            columns={zoneColumns}
            data={data.service_zones}
            getRowId={(r) => r.id}
            dense
          />
        </CardBody>
      </Card>
    </div>
  );
}
